// Mahjong runtime scene. Owns the live `MahjongState` and wires the pieces of
// the play loop together: the rAF paint loop (dirty-flag gated), the pointer
// InputHandler (tap → discard), a `SceneDriver` (solo bots, or the server),
// and the three DOM surfaces (ActionBar, CoachPanel, HandEndModal). The scene
// is glue — who advances the hand lives behind `sceneDrivers.ts`, the tap math
// in SceneCallbacks, and the painting in SceneRender.
//
// Solo and online differ in exactly one place: which driver `mount` builds.
// Everything below that — render, hit-test, coach, action bar — is identical,
// because a filtered snapshot rebuilds into the same `MahjongState` solo deals
// locally (see `network/snapshotToState.ts`). The Coach stays enabled online:
// it only ever reads the local seat's own hand, which is the one hand the
// server does send us in full.

import { TurnSignal } from '@bn-games/audio-core';
import type { RoomClient } from '@bn-games/room-client';
import { themeToRenderPalette } from '../render/palette.js';
import { renderScene } from './SceneRender.js';
import { RafLoop } from './RafLoop.js';
import { resolveDiscardFromTap } from './SceneCallbacks.js';
import { LOCAL_SEAT } from './Bootstrap.js';
import { createSceneDriver, isOnlineMount } from './sceneDrivers.js';
import { addOpponentCandidates } from './opponentCandidates.js';
import { THEMES } from '../settings/themes.js';
import { TableViewport } from './TableViewport.js';
import { ActionBar } from '../ui/ActionBar.js';
import { CoachPanel } from '../ui/CoachPanel.js';
import { HandEndModal } from '../ui/HandEndModal.js';
import { buildCoachAdvice } from '../coach/Coach.js';
import type { MountOptions, SceneDriver, SceneDriverHooks } from './sceneDrivers.js';
import type { LayoutManager } from '../layout/LayoutManager.js';
import type { SettingsManager } from '../settings/SettingsManager.js';
import type { MahjongState } from '../game/GameState.js';
import { DEALER } from '../game/stateCore.js';
import type { SeatClaims } from '../game/Claims.js';
import type { Seat } from '../game/types.js';
import type { MatchState } from '../match/MatchState.js';
import type { KongDecision } from '../ai/types.js';
import type { ClientMessage, ServerMessage } from '../network/protocol.js';

export type {
  MountOptions,
  OnlineMountOptions,
  SoloMountOptions,
} from './sceneDrivers.js';

/**
 * Whether the table is waiting on the local seat right now. A claim window is
 * deliberately not "your turn": it's an optional reaction to someone else's
 * discard, and the action bar already surfaces it with its own countdown.
 */
function isLocalTurnForSignal(state: MahjongState, localSeat: Seat): boolean {
  return state.phase === 'awaiting-discard' && state.turn === localSeat;
}

export interface GameSceneOptions {
  readonly canvas: HTMLCanvasElement;
  readonly layoutManager: LayoutManager;
  readonly settingsManager: SettingsManager;
  /** Classical solo only: called instead of dealing a next hand once a match finishes. */
  readonly onMatchComplete?: () => void;
  /** The invite pill's bottom edge in canvas space, or null while it isn't showing. */
  readonly pillBottom?: () => number | null;
  /** The on-canvas pause gear was tapped (same as Escape). */
  readonly onPause: () => void;
}

export class GameScene {
  private state: MahjongState | null = null;
  private matchState: MatchState | null = null;
  private driver: SceneDriver | null = null;
  /** Online table roster access for the hand-end "add opponent" control — `null` solo. */
  private room: RoomClient<ClientMessage, ServerMessage> | null = null;
  private isDirty = true;
  private tableViewport: TableViewport | null = null;
  private readonly raf = new RafLoop(
    () => this.drawFrame(),
    () => this.markDirty(),
  );
  private readonly actionBar = new ActionBar();
  // Arrows capture `this` lazily — none of these fire until well after the
  // constructor body has run, so they are safe despite initializing here.
  private readonly coach = new CoachPanel(() => this.dismissCoach());
  private readonly handEnd = new HandEndModal({ onNextHand: () => this.driver?.nextHand() });
  private readonly hooks: SceneDriverHooks = {
    getState: () => this.requireState(),
    setState: (next) => this.setState(next),
    getMatchState: () => this.matchState,
    setMatchState: (next) => this.setMatchState(next),
    onLocalTurn: (canDeclareTsumo, kongOptions) => this.showLocalTurn(canDeclareTsumo, kongOptions),
    onLocalClaimWindow: (claims) => this.showLocalClaimWindow(claims),
    onHandEnd: (result, handScore, deal) =>
      this.handEnd.open(
        result,
        handScore,
        this.matchState?.dealer ?? DEALER,
        deal ?? null,
        addOpponentCandidates(this.room, this.localSeat()),
      ),
    clearLocalUi: () => this.actionBar.clear(),
    closeHandEnd: () => this.handEnd.close(),
  };

  /**
   * "Your turn" cue. Driven off `setState` rather than the `onLocalTurn` hook:
   * that hook fires again for the same turn (a kong keeps the turn, a resumed
   * table re-offers it), while the state carries the one fact the cue is about.
   */
  private readonly turnSignal = new TurnSignal({
    isEnabled: () => this.opts.settingsManager.get().turnStartSound,
  });

  constructor(private readonly opts: GameSceneOptions) {}

  mount(opts: MountOptions): void {
    // Online there is nothing to paint until the first STATE_SYNC lands; the
    // draw loop already no-ops on a null state, so no placeholder deal is needed.
    const solo = isOnlineMount(opts) ? null : opts;
    this.state = solo?.state ?? null;
    this.matchState = solo?.matchState ?? null;
    this.room = isOnlineMount(opts) ? opts.room : null;
    this.isDirty = true;
    // A fresh table decides turn ownership from scratch — drop the last
    // table's answer so an opening turn on our seat still sounds.
    this.turnSignal.reset();
    this.raf.start();
    this.driver = createSceneDriver(opts, this.hooks, () => this.opts.onMatchComplete?.());
    this.tableViewport = new TableViewport({
      canvas: this.opts.canvas,
      layoutManager: this.opts.layoutManager,
      onTap: (x, y) => this.handleTap(x, y),
      onDirty: () => this.markDirty(),
      getState: () => this.state,
      getLocalSeat: () => this.localSeat(),
      onPause: this.opts.onPause,
    });
    // Solo `mount` adopts its opening deal directly rather than through
    // `setState`, so a hand we deal ourselves into needs the cue here. Online
    // there is no state yet — the first snapshot goes through `setState`.
    if (this.state !== null) {
      this.turnSignal.update(isLocalTurnForSignal(this.state, this.localSeat()));
    }
    this.driver.start();
  }

  unmount(): void {
    this.raf.stop();
    this.tableViewport?.dispose();
    this.tableViewport = null;
    this.driver?.dispose();
    this.driver = null;
    this.actionBar.clear();
    this.coach.clear();
    this.handEnd.close();
    this.state = null;
    this.room = null;
  }

  markDirty(): void {
    this.isDirty = true;
  }

  /** A table is dealt and being played — solo or online; a lesson never counts. */
  isInProgress(): boolean {
    return this.state !== null && this.driver !== null;
  }

  /**
   * The pause menu opened or closed (`scenes/PauseHost.ts`). Only the driver
   * has anything to freeze; the rAF loop keeps running so the table stays
   * painted behind the card, and it is dirty-gated, so a still table costs
   * nothing. Safe to call with no scene mounted — the lesson scene shares this
   * canvas and has no driver, and no timers of its own to stop.
   */
  setPaused(paused: boolean): void {
    this.driver?.setPaused?.(paused);
  }

  /** The point of view every renderer and hit-test rotates around. */
  private localSeat(): Seat {
    return this.driver?.localSeat ?? LOCAL_SEAT;
  }

  private requireState(): MahjongState {
    if (this.state === null) throw new Error('GameScene: no active state');
    return this.state;
  }

  private setState(next: MahjongState): void {
    this.state = next;
    this.isDirty = true;
    this.turnSignal.update(isLocalTurnForSignal(next, this.localSeat()));
  }

  private setMatchState(next: MatchState | null): void {
    this.matchState = next;
    this.isDirty = true;
  }

  private handleTap(x: number, y: number): void {
    if (this.state === null || this.driver === null) return;
    const cfg = this.opts.layoutManager.getConfig();
    const tileId = resolveDiscardFromTap(this.state, cfg, x, y, this.localSeat());
    if (tileId !== null) this.driver.discard(tileId);
  }

  private showLocalTurn(canDeclareTsumo: boolean, kongOptions: readonly KongDecision[]): void {
    const driver = this.driver;
    if (driver !== null && (canDeclareTsumo || kongOptions.length > 0)) {
      this.actionBar.showOwnTurnOptions(
        canDeclareTsumo,
        kongOptions,
        () => driver.declareTsumo(),
        (kong) => driver.declareKong(kong),
      );
    } else {
      this.actionBar.clear();
    }
  }

  private showLocalClaimWindow(claims: SeatClaims): void {
    const driver = this.driver;
    if (driver === null) return;
    this.actionBar.showClaimWindow(
      this.localSeat(),
      claims,
      (claim) => driver.resolveClaim(claim),
      () => driver.resolveExposedKong(),
    );
  }

  /** Turns the `coachEnabled` setting off — same switch the Settings screen's
   *  coach toggle uses, so the panel stays in sync no matter which surface
   *  turned it off, and re-enabling from Settings brings it right back. */
  private dismissCoach(): void {
    this.opts.settingsManager.update({ coachEnabled: false });
    this.markDirty();
  }

  private drawFrame(): void {
    if (this.state === null || !this.isDirty || this.tableViewport === null) return;
    this.isDirty = false;
    const ctx = this.opts.canvas.getContext('2d');
    if (ctx === null) throw new Error('GameScene: canvas 2D context unavailable');
    const settings = this.opts.settingsManager.get();
    const palette = themeToRenderPalette(THEMES[settings.theme]);
    const layout = this.opts.layoutManager.getConfig();
    const { viewport } = this.tableViewport;
    const seat = this.localSeat();
    const pillBottom = this.opts.pillBottom?.() ?? null;
    renderScene(ctx, this.state, layout, palette, viewport, this.matchState, seat, pillBottom);
    this.tableViewport.drawControls(ctx, palette);
    this.coach.update(buildCoachAdvice(this.state, seat), layout, settings.coachEnabled);
  }
}
